import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch as useDispatchBase, useSelector as useSelectorBase } from 'react-redux';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';

import auth from '@/stores/authSlice';
import classes from '@/stores/classSlice';
import history from '@/stores/historySlice';
import kpi from '@/stores/kpiSlice';
import storage from '@/stores/storage';
import ui from '@/stores/uiSlice';

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['classes'],
  version: 1,
};

const defaultReducer = combineReducers({
  auth,
  history,
  ui,
  classes,
  kpi,
});

type ReducerType = typeof defaultReducer;
type PersistReducerType = Extract<ReducerType, typeof reducer>;

const persistedReducer = persistReducer(persistConfig, defaultReducer);

const reducer = typeof window !== 'undefined' ? persistedReducer : defaultReducer;

export const store = configureStore({
  reducer: reducer as PersistReducerType,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;

type AppDispatch = typeof store.dispatch;

export const useDispatch = () => useDispatchBase<AppDispatch>();

export const useSelector = <T = unknown>(selector: (state: RootState) => T): T =>
  useSelectorBase<RootState, T>(selector);
